import { supabase } from './supabase';

/**
 * Junk on a lead chat (Gary, 19 Oct), as the campaign tracker and the Telegram lead alert mark it: not a real
 * enquiry, saved as the lead's status lost with the reason, so the tracker counts it as junk (Test entry is
 * left out of its counts). Someone with a confirmed booking was a real lead and cannot be junk. The reasons are the
 * tracker's (supabase/functions/_shared/tracker.ts JUNK_REASONS and TEST_REASON); keep the two lists the same.
 */
export const JUNK_REASONS = ['Job seeker', 'Business opportunity', 'Selling to us', 'Out of our area', 'Fake or wrong details', 'Duplicate', 'Test entry'] as const;
export type JunkReason = typeof JUNK_REASONS[number];

/** leadId null: a chat with no lead yet (a WhatsApp job seeker, Gary 19 Oct): the chat is noted and closed only. */
export async function markJunk(chatId: string, leadId: string | null, reason: JunkReason): Promise<void> {
  if (!leadId) return closeAsJunk(chatId, reason);
  const { data: lead, error } = await supabase.from('leads').select('status, customer_id').eq('id', leadId).maybeSingle();
  if (error || !lead) throw new Error('Lead not found.');
  // A live booking a person confirmed is what makes someone a real customer: a converted lead whose only booking
  // was cancelled can be junk (Gary, 19 Oct: Chimwemwe, a job seeker who booked by mistake, was refused after
  // Barry cancelled it). A website booking still pending was never confirmed by anyone; job seekers fill in the
  // booking page (Gary, 19 Oct: Moleen), so Junk cancels it quietly instead of refusing.
  let unconfirmed: string[] = [];
  if (lead.customer_id) {
    const { data: live } = await supabase.from('bookings').select('id, status, source')
      .eq('customer_id', lead.customer_id as string).is('cancelled_at', null).neq('status', 'cancelled');
    const rows = (live ?? []) as { id: string; status: string; source: string | null }[];
    if (rows.some((b) => b.status !== 'pending' || b.source !== 'website')) throw new Error('This lead has a confirmed booking, so it cannot be junk.');
    unconfirmed = rows.map((b) => b.id);
  } else if (lead.status === 'converted' || lead.status === 'won') throw new Error('This lead has a booking, so it cannot be junk.');

  for (const id of unconfirmed) {
    const { data, error: cancelError } = await supabase.functions.invoke('booking-manage', {
      body: { operation: 'cancel', id, cancellation_reason: 'other', cancellation_notes: `Junk: ${reason}`, skip_notification: true },
    });
    if (cancelError || (data as { error?: string } | null)?.error) throw new Error('The website booking did not cancel, so nothing was marked. Try again.');
  }

  const now = new Date().toISOString();
  const { error: saveError } = await supabase.from('leads')
    .update({ status: 'lost', outcome_reason: reason, status_changed_at: now, updated_at: now }).eq('id', leadId);
  if (saveError) throw new Error('That did not save. Try again.');

  return closeAsJunk(chatId, reason);
}

/** A note for the team, then the chat closes: a junk enquiry needs no reply. */
async function closeAsJunk(chatId: string, reason: JunkReason): Promise<void> {
  await supabase.functions.invoke('messaging-send', { body: { action: 'note', conversation_id: chatId, text: `Marked junk: ${reason}` } });
  const { error: closeError } = await supabase.functions.invoke('messaging-send', {
    body: { action: 'close', conversation_id: chatId, outcome: 'closed', reason: `Junk: ${reason}` },
  });
  if (closeError) throw new Error('Marked junk, but the chat did not close. Close it from Info.');
}
